import { lazy, Suspense, ComponentType } from 'react'
import { Navigate, Outlet } from 'react-router-dom';
// auth
import { AuthGuard } from 'src/auth/guard'
import { AuthUserType } from 'src/auth/types'
import { useAuthContext } from 'src/auth/hooks'
// layouts
import DashboardLayout from 'src/layouts/dashboard'
// components
import { LoadingScreen } from 'src/components/loading-screen'

// ----------------------------------------------------------------------

// Component that handles no-match routes - waits for auth to load before redirecting
function DashboardNoMatch() {
  const { loading, user } = useAuthContext();
  
  // Wait while:
  // 1. Auth is still initializing (loading === true)
  // 2. OR user exists but full data (access) hasn't loaded yet
  const isLoadingUserData = user && !user.access;
  
  if (loading || isLoadingUserData) {
    return <LoadingScreen />;
  }
  
  // Once everything is loaded, redirect to dashboard
  return <Navigate to="/dashboard" replace />;
}

// ----------------------------------------------------------------------
// Helper pour le lazy loading avec prefetch
const lazyWithPrefetch = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> & { preload: () => Promise<void> } => {
  const Component = lazy(factory) as any;
  Component.preload = factory;
  return Component;
};

// OVERVIEW - Préchargé car c'est la page d'accueil
const IndexPage = lazyWithPrefetch(() => import('src/pages/dashboard/app'))
// Précharger immédiatement la page d'accueil
IndexPage.preload?.();

// const IndexNewPage = lazy(() => import('src/pages/dashboard/newApp'))
// ALL PAGES
const allPages = {
  user: {
    list: lazyWithPrefetch(() => import('src/pages/dashboard/user/list')),
    account: lazyWithPrefetch(() => import('src/pages/dashboard/user/account')),
    config: lazyWithPrefetch(() => import('src/pages/dashboard/user/config')),
    new: lazyWithPrefetch(() => import('src/pages/dashboard/user/new')),
    role: lazyWithPrefetch(() => import('src/pages/dashboard/user/role')),
    register: lazyWithPrefetch(() => import('src/pages/dashboard/user/register'))
  },
  customer: {
    list: lazyWithPrefetch(() => import('src/pages/dashboard/customer/list')),
    new: lazyWithPrefetch(() => import('src/pages/dashboard/customer/new')),
    ':id': lazyWithPrefetch(() => import('src/pages/dashboard/customer/detail'))
  },
  product: {
    root: lazyWithPrefetch(() => import('src/pages/dashboard/product/list')),
    ':id': lazyWithPrefetch(() => import('src/pages/dashboard/product/details')),
    'supply/:id': lazyWithPrefetch(() => import('src/pages/dashboard/product/supply')),
    'supply': lazyWithPrefetch(() => import('src/pages/dashboard/product/supplylist')),
    // inventory: lazy(() => import('src/pages/dashboard/product/inventory')),
  },
  order: {
    sales: lazyWithPrefetch(() => import('src/pages/dashboard/order/sales')),
    invoices: lazyWithPrefetch(() => import('src/pages/dashboard/invoice/list')),
    'invoice/:id': lazyWithPrefetch(() => import('src/pages/dashboard/invoice/details')),
    'return/:id': lazyWithPrefetch(() => import('src/pages/dashboard/invoice/return')),
    'return-no-sale': lazyWithPrefetch(() => import('src/pages/dashboard/invoice/return-no-sale')),
  },
  cashRegister: {
    list: lazyWithPrefetch(() => import('src/pages/dashboard/cash-register/list')),
    fund: lazyWithPrefetch(() => import('src/pages/dashboard/cash-register/fund')),
    ':id': lazyWithPrefetch(() => import('src/pages/dashboard/cash-register/details'))
  },
  configManagement: lazyWithPrefetch(() => import('src/pages/dashboard/config-management')),
  reports: lazyWithPrefetch(() => import('src/pages/dashboard/reports')),
  inventory: lazyWithPrefetch(() => import('src/pages/dashboard/inventory')),
} as any

// ----------------------------------------------------------------------

/**
 * Generates dashboard routes based on the authenticated user's access levels.
 *
 * This function dynamically constructs a list of route configurations
 * for the dashboard, including nested routes for specific user roles
 * and access permissions.
 *
 * @param {AuthUserType} user - The authenticated user object containing access and role information.
 * @returns {any[]} An array of route configurations to be used within the application router.
 */
export const dashboardRoutes = (user: AuthUserType): any[] => {
  const toReturn = [
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </DashboardLayout>
        </AuthGuard>
      ),
      children: [
        { element: <IndexPage />, index: true },
        // { path: 'new', element: <IndexNewPage /> }
      ],
    },

    // No match dashboard - wait for auth to load before redirecting
    { path: '*', element: <DashboardNoMatch /> }
  ] as any[]
  const access = user?.access || {}
  access.cashRegister = [{ name: 'fund', label: 'fund' }, { name: 'list', label: 'list' }, { name: ':id' }]
  // access.configManagement = [{ name: 'root', label: 'root' }]
  if (!user?.currentRegister?.currentSession){
    // allPages.order.sales = allPages.cashRegister.fund
  }
  /*
  if (access.product?.[0] && !access.product.some((p: any) => p?.name === 'inventory')) {
    access.product = [...access.product, { name: 'inventory', label: 'inventory' }]
  }
  */
  if (access) {
    Object.entries(access).forEach(([key, value]: [string, any]) => {
      if (allPages[key] && value && value[0]) {
        const KeyPage = allPages[key][value[0].name]
        if (KeyPage) {
          const keyChild = {
            path: key,
            children: [{ element: <KeyPage />, index: true, path: '' }]
          }
          value.forEach(({ name }: { name: string }) => {
            const ValPage = allPages[key][name]
            if (ValPage) keyChild.children.push({
              index: false, path: name !== 'root' && name || '',
              element: <ValPage />
            })
          })
          const ReturnNoSalePage = allPages.order['return-no-sale']
          if (key === 'user' && user?.role === 'Admin')
            keyChild.children.push({ index: false, path: 'config', element: <allPages.user.config /> })
          if (key === 'order' && ReturnNoSalePage && !keyChild.children.find((child: any) => child.path === 'return-no-sale')) {
            keyChild.children.push({
              index: false,
              path: 'return-no-sale',
              element: <ReturnNoSalePage />
            });
          }
          // Add customer detail route (always add it)
          const CustomerDetailPage = allPages.customer?.[':id']
          if (key === 'customer' && CustomerDetailPage) {
            keyChild.children.push({
              index: false,
              path: ':id',
              element: <CustomerDetailPage />
            });
          }
          toReturn[0].children.push(keyChild)
        }
      }
    })

    // Add reports route for managers
    if (user?.role === 'Manager' || user?.role === 'Admin') {
      const ReportsPage = allPages.reports;
      if (ReportsPage) {
        toReturn[0].children.push({
          index: false,
          path: 'reports',
          element: <ReportsPage />
        });
      }

      const InventoryPage = allPages.inventory;
      if (InventoryPage) {
        toReturn[0].children.push({
          index: false,
          path: 'inventory',
          element: <InventoryPage />
        });
      }
    }

    // Add config-management route for admin
    if (user?.role === 'Admin') {
      const ConfigManagementPage = allPages.configManagement;
      if (ConfigManagementPage) {
        toReturn[0].children.push({
          index: false,
          path: 'config-management',
          element: <ConfigManagementPage />
        });
      }
    }
  }
  return toReturn
}
